import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NavigationCategory,
  NotificationData,
} from '../models/OwnerLayout';
import { ownerLayoutService } from '../services/OwnerLayoutService';
import { tokenManager } from '../../../infrastructure/api/tokenManager';
import {
  ChartBarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CogIcon,
  HomeIcon,
  CurrencyDollarIcon,
  RectangleGroupIcon,
  BellIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  ClockIcon,
  AcademicCapIcon,
  ChatBubbleBottomCenterTextIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// 모듈 정보 타입
interface ModuleInfo {
  id: string;
  name: string;
  display_name: string;
  category: string;
}

interface PlanInfo {
  id: string;
  name: string;
  display_name: string;
  price: number;
  billing_cycle: string;
}

interface PlanInfoResponse {
  success: boolean;
  has_subscription: boolean;
  plan: PlanInfo | null;
  modules: ModuleInfo[];
}

/**
 * 오너 레이아웃 관리 훅
 */
// 메뉴 아이템과 모듈 매핑 (DB의 service_modules.id와 일치해야 함)
// DB 모듈: member, seat, attendance, access_log, notification, learning,
//          payment, ticket, settings, face_recognition, penalty, schedule,
//          parent_report, advanced_analytics, multi_branch, api_access
/**
 * 사이드바 배지용 조회. 액세스 토큰이 만료돼 401 이 나면 공용 갱신(tokenManager)으로
 * 한 번 갱신하고 다시 부른다.
 * 🔴 2026-09-24 — 종전엔 맨 fetch 라 토큰이 만료(1시간)되면 매분 401 만 받고
 *    배지 숫자가 그 자리에 멈춰 있었다(우장산점 '사유서 배지 멈춤').
 *    갱신 실패는 조용히 넘긴다 — 배지 하나 때문에 화면이 튕기면 안 된다.
 */
const fetchBadge = async (url: string): Promise<Response> => {
  const call = () =>
    fetch(url, { headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` } });
  const res = await call();
  if (res.status !== 401 || !tokenManager.getRefreshToken()) return res;
  try {
    await tokenManager.refreshAccessToken();
  } catch {
    return res;
  }
  return call();
};

const MENU_MODULE_MAPPING: Record<string, string[]> = {
  // 운영 관리 - 기본 모듈
  '실시간 현황': ['seat', 'member'],  // 항상 표시 (기본)
  '출석부': ['seat', 'member'],
  '회원 관리': ['member'],
  '입출입 기록': ['access_log'],
  '퇴실 미기록 확인': ['access_log'],
  '알림톡 발송 이력': ['notification'],

  // 학습 관리 - learning 모듈
  '대시보드': ['learning'],
  '학습 분석': ['learning'],
  '목표 관리': ['learning'],
  '상담 기록': ['learning'],

  // 출석 관리 - attendance 모듈
  '통계/리포트': ['attendance'],
  '스케줄 설정': ['attendance', 'schedule'],
  '미출석 모니터링': ['attendance'],
  '교시별 출결 현황': ['attendance'],
  '벌점 관리': ['penalty', 'attendance'],
  '사유서 관리': ['attendance', 'schedule'],

  // 시설 관리 - seat 모듈
  '좌석 관리': ['seat'],
  '사물함 관리': ['seat'],
  '통합 레이아웃 관리': ['seat'],
  '전체 건물 맵': ['multi_branch', 'seat'],
  '그룹 공간 관리': ['seat'],
  '게이트 관리': ['seat', 'access_log'],

  // 비즈니스 - payment, ticket 모듈
  '이용권 관리': ['payment', 'ticket'],
  'VIP/단골 관리': ['advanced_analytics'],
  '통계 리포트': ['advanced_analytics'],
  '매출': ['advanced_analytics'],
  '사용량 관리': ['payment', 'ticket'],

  // 설정 - settings 모듈
  '시설 정보': ['settings'],
  '운영 규칙': ['settings'],
  '직원 관리': ['settings', 'member'],
  '과목 관리': ['learning'],
  '연동 설정': ['api_access', 'settings'],
};

export const useOwnerLayout = () => {
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    '운영 관리',
  ]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(
    () => ownerLayoutService.loadSidebarState()
  );
  const [notifications, setNotifications] = useState<NotificationData>({
    alerts: 0,
    newMembers: 0,
    checkIns: 0,
  });
  const [parentNotificationCount, setParentNotificationCount] = useState(0);
  const [absenceCount, setAbsenceCount] = useState(0);
  const [exceptionPendingCount, setExceptionPendingCount] = useState(0);
  const [schedulePendingCount, setSchedulePendingCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  // 입금 안 된 알림톡 사용료 청구서 수 — 원장이 메일을 안 봐도 알아채게 (2026-09-09)
  const [unpaidBillCount, setUnpaidBillCount] = useState(0);
  // 오늘 순찰 누락 칸 수 (2026-09-24)
  const [patrolMissedCount, setPatrolMissedCount] = useState(0);
  const [organizationName, setOrganizationName] = useState(
    ownerLayoutService.getOrganizationName()
  );
  const [availableModules, setAvailableModules] = useState<string[]>([]);
  const [currentPlan, setCurrentPlan] = useState<PlanInfo | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  /**
   * 플랜/모듈 정보 가져오기 - 완전 비활성화 (모든 메뉴 표시)
   */
  const fetchPlanInfo = useCallback(async () => {
    // 아무것도 하지 않음 - 모든 메뉴 표시
    setPlanLoading(false);
  }, []);

  /**
   * 메뉴 아이템이 현재 플랜에서 사용 가능한지 확인
   * TODO: 플랜별 제한 기능은 추후 구현 예정 - 현재는 모든 메뉴 표시
   */
  const isMenuItemAvailable = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_menuName: string): boolean => {
      // 모든 메뉴 항상 표시 (플랜 제한 비활성화)
      return true;
    },
    []
  );

  /**
   * 알림 데이터 가져오기
   * 최적화: 무거운 API 호출 제거 (seats-management, members)
   * 사이드바 뱃지 숫자 비활성화로 불필요한 서버 부하 제거
   */
  const fetchNotifications = useCallback(async () => {
    // 성능 최적화: 사이드바 뱃지를 위한 무거운 API 호출 제거
    // 기존에 30초마다 전체 좌석/회원 데이터를 가져오던 것을 제거
    // 뱃지 숫자가 필요하면 경량 API를 추후 구현

    // 사유서 처리 대기 카운트 (가벼움 — group by count)
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetchBadge(`${apiBase}/v1/owner/schedule-exceptions/pending-count`);
      if (res.ok) {
        const data = await res.json();
        setExceptionPendingCount(Number(data?.count ?? 0));
      }
    } catch {
      // 사이드바 카운트는 무시 가능
    }

    // ⭐ 2026-10-10 — 스케줄 승인 대기(주간 변경 요청 + 월간 계획) 학생 수.
    //   종전엔 '스케줄 설정'에 숫자가 없어 원장이 화면을 열어야만 알았다
    //   (2026-07-31 원장 화면에 안 뜬 요청이 최대 34일 방치된 적 있음).
    //   🔴 이 라우터는 prefix 가 '/api/v1/monthly-plans' — '/v1/...' 로 붙이면 404.
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const root = (apiBase || '/api').replace(/\/api$/, '');
      const res = await fetchBadge(`${root}/api/v1/monthly-plans/pending/count`);
      if (res.ok) {
        const data = await res.json();
        setSchedulePendingCount(Number(data?.total ?? 0));
      }
    } catch {
      // 무시 가능
    }

    // 학부모 소통 안읽음 카운트 (가벼움 — count 1건)
    // owner_posts 라우터는 /api/v1 에만 등록됨 (/v1 미등록) → /api 접두 필수
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetchBadge(`${apiBase || '/api'}/v1/owner/posts/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setChatUnreadCount(Number(data?.data?.unread_count ?? 0));
      }
    } catch {
      // 무시 가능
    }

    // 알림톡 사용료 입금 대기 카운트 (가벼움 — count 1건)
    // 🔴 원장이 [사용량 관리]에 직접 들어가야만 청구를 알 수 있어서, 메일을 안 보면
    //    며칠씩 모르고 지나갔다. 사이드바에 숫자를 띄워 눈에 걸리게 한다.
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetchBadge(
        `${apiBase || '/api'}/v1/owner/billing/alimtalk-bills/unpaid-count`,
      );
      if (res.ok) {
        const data = await res.json();
        setUnpaidBillCount(Number(data?.count ?? 0));
      }
    } catch {
      // 배지 하나 때문에 사이드바가 깨지면 안 된다
    }

    // 오늘 순찰 누락 칸 수 — 순찰 계획을 안 쓰는 매장은 늘 0
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetchBadge(`${apiBase || '/api'}/v1/owner/patrol-qr/missed-count`);
      if (res.ok) {
        const data = await res.json();
        setPatrolMissedCount(Number(data?.count ?? 0));
      }
    } catch {
      // 무시 가능
    }
  }, []);

  /**
   * 플랜 정보 초기화
   */
  useEffect(() => {
    fetchPlanInfo();
  }, [fetchPlanInfo]);

  /**
   * 알림 데이터 폴링 — 사유서 카운트만 가벼운 API 호출 (60초)
   */
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000); // 1분
    // 사유서 화면에서 처리하면 1분을 기다리지 않고 바로 다시 센다
    window.addEventListener('exception-counts-changed', fetchNotifications);
    window.addEventListener('schedule-counts-changed', fetchNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener('exception-counts-changed', fetchNotifications);
      window.removeEventListener('schedule-counts-changed', fetchNotifications);
    };
  }, [fetchNotifications]);

  /**
   * 사이드바 열릴 때 body 스크롤 막기
   */
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isSidebarOpen]);

  /**
   * 모바일 ESC 키로 사이드바 닫기
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSidebarOpen]);

  /**
   * 화면 크기 변경 감지
   */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * 데스크톱 사이드바 상태 저장
   */
  useEffect(() => {
    ownerLayoutService.saveSidebarState(isDesktopSidebarCollapsed);
  }, [isDesktopSidebarCollapsed]);

  /**
   * 조직명 변경 이벤트 리스너 및 초기 로드
   */
  useEffect(() => {
    // 초기 로드 시 캐시에서 조직명 가져오기
    const updateOrgName = () => {
      const newName = ownerLayoutService.getOrganizationName();
      setOrganizationName(newName);
    };

    // 컴포넌트 마운트 시 즉시 업데이트
    updateOrgName();

    // 이벤트 리스너 등록
    window.addEventListener('organizationNameUpdated', updateOrgName);

    // storage 이벤트 리스너 추가 (다른 탭에서 변경 감지)
    window.addEventListener('storage', updateOrgName);

    return () => {
      window.removeEventListener('organizationNameUpdated', updateOrgName);
      window.removeEventListener('storage', updateOrgName);
    };
  }, []);

  /**
   * 전체 네비게이션 카테고리 구성 (필터링 전)
   */
  const allNavigationCategories: NavigationCategory[] = [
    {
      title: '운영 관리',
      icon: ChartBarIcon,
      items: [
        {
          name: '실시간 현황',
          href: ownerLayoutService.getOwnerPath('/owner/dashboard'),
          icon: ChartBarIcon,
          // badge 제거 - 성능 최적화
        },
        {
          name: '출석부',
          href: ownerLayoutService.getOwnerPath('/owner/seat-operation'),
          icon: BuildingOfficeIcon,
          // badge 제거 - 성능 최적화
        },
        {
          name: '회원 관리',
          href: ownerLayoutService.getOwnerPath('/owner/members'),
          icon: UserGroupIcon,
          // badge 제거 - 성능 최적화
        },
        {
          name: '입출입 기록',
          href: ownerLayoutService.getOwnerPath('/owner/entrance-exit'),
          icon: ClockIcon,
        },
        {
          name: '퇴실 미기록 확인',
          href: ownerLayoutService.getOwnerPath('/owner/entrance-anomalies'),
          icon: ExclamationTriangleIcon,
        },
        {
          name: '알림톡 발송 이력',
          href: ownerLayoutService.getOwnerPath('/owner/notification-history'),
          icon: ChatBubbleLeftRightIcon,
        },
        {
          name: '학부모 소통',
          href: ownerLayoutService.getOwnerPath('/owner/parent-communication'),
          icon: ChatBubbleBottomCenterTextIcon,
          badge: chatUnreadCount || undefined,
        },
        {
          name: '신청 관리',
          href: ownerLayoutService.getOwnerPath('/owner/applications'),
          icon: ClipboardDocumentCheckIcon,
        },
        {
          // 공휴일·수능·학사·매장 일정 달력 (2026-07-29)
          name: '월간 일정',
          href: ownerLayoutService.getOwnerPath('/owner/calendar'),
          icon: CalendarDaysIcon,
        },
        {
          // 구역 QR 찍으면 순찰 완료 기록 (2026-09-23)
          name: '순찰 QR',
          href: ownerLayoutService.getOwnerPath('/owner/patrol-qr'),
          // 오늘 누락된 순찰 칸 — 알림 대신 원장 눈에 걸리게 (2026-09-24)
          badge: patrolMissedCount > 0 ? patrolMissedCount : undefined,
          icon: ClipboardDocumentCheckIcon,
        },
        {
          // 순찰 때 표시한 졸음·핸드폰 등이 쌓이는 곳 (2026-10-04) — 화면은 있었는데 메뉴가 없어 주소로만 열렸다
          name: '순찰 노트',
          href: ownerLayoutService.getOwnerPath('/owner/patrol-notes'),
          icon: ClipboardDocumentCheckIcon,
        },
      ],
    },
    {
      title: '학습 관리',
      icon: AcademicCapIcon,
      items: [
        {
          name: '학습 분석',
          href: ownerLayoutService.getOwnerPath('/owner/study-analytics'),
          icon: ChartBarIcon,
        },
        {
          name: '목표 관리',
          href: ownerLayoutService.getOwnerPath('/owner/goal-management'),
          icon: ClipboardDocumentCheckIcon,
        },
        {
          name: '상담 기록',
          href: ownerLayoutService.getOwnerPath('/owner/consultation-records'),
          icon: ChatBubbleBottomCenterTextIcon,
        },
        {
          // ⭐ 상담 대시보드 — 페이지·백엔드는 완성됐으나 AI 부분이 정비 중이라 '준비중' 표기
          name: '상담 대시보드 (준비중)',
          href: ownerLayoutService.getOwnerPath('/owner/consultation-dashboard'),
          icon: ChartBarIcon,
        },
      ],
    },
    {
      title: '출석 관리',
      icon: ClipboardDocumentCheckIcon,
      items: [
        {
          name: '스케줄 설정',
          href: ownerLayoutService.getOwnerPath('/owner/attendance/schedule'),
          icon: CalendarDaysIcon,
          badge: schedulePendingCount > 0 ? schedulePendingCount : undefined,
        },
        {
          name: '교시 시간표 설정',
          href: ownerLayoutService.getOwnerPath('/owner/period-settings'),
          icon: CalendarDaysIcon,
        },
        // 🔴 '미출석 모니터링' 메뉴 제거 (2026-09-10 대표 지시)
        //    출석부에서 미출석 학생과 전화 걸기가 이미 되어 중복이었다.
        //    실사용도 거의 없었다 — 연락 기록 6건(마지막 8/29), 연락 시도 5건(마지막 5/13).
        //    🔴 페이지(라우트)는 남겨 뒀다. 주소 /owner/attendance/monitoring 로 들어가면
        //       지난 연락 기록 11건을 여전히 볼 수 있다. 메뉴에서만 빠진 것이다.
        {
          name: '벌점 관리',
          href: ownerLayoutService.getOwnerPath('/owner/penalty'),
          icon: ExclamationTriangleIcon,
        },
        {
          name: '사유서 관리',
          href: ownerLayoutService.getOwnerPath('/owner/exception-requests'),
          icon: DocumentTextIcon,
          badge: exceptionPendingCount > 0 ? exceptionPendingCount : undefined,
        },
      ],
    },
    {
      title: '시설 관리',
      icon: BuildingOfficeIcon,
      items: [
        {
          name: '좌석 관리',
          href: ownerLayoutService.getOwnerPath('/owner/seats'),
          icon: RectangleGroupIcon,
        },
        {
          name: '사물함 관리',
          href: ownerLayoutService.getOwnerPath('/owner/lockers'),
          icon: RectangleGroupIcon,
        },
        {
          name: '통합 레이아웃 관리',
          href: ownerLayoutService.getOwnerPath('/owner/integrated-layout'),
          icon: ShieldCheckIcon,
        },
        {
          name: '게이트 관리',
          href: ownerLayoutService.getOwnerPath('/owner/gates'),
          icon: ShieldCheckIcon,
        },
      ],
    },
    {
      title: '비즈니스',
      icon: CurrencyDollarIcon,
      items: [
        {
          name: '이용권 관리',
          href: ownerLayoutService.getOwnerPath('/owner/payment-management'),
          icon: CurrencyDollarIcon,
        },
        {
          name: '매출',
          href: ownerLayoutService.getOwnerPath('/owner/analytics'),
          icon: PresentationChartLineIcon,
        },
        {
          name: '사용량 관리',
          href: ownerLayoutService.getOwnerPath('/owner/usage'),
          icon: ChartBarIcon,
          badge: unpaidBillCount > 0 ? unpaidBillCount : undefined,
        },
      ],
    },
    {
      // 직원 관련 메뉴가 운영 관리와 설정 두 군데로 흩어져 있어 한 묶음으로 모음 (2026-08-25)
      title: '직원 관리',
      icon: UserGroupIcon,
      items: [
        {
          // 직원 출입 기록 + 총무 업무를 한 곳에 모은 화면 (2026-08-25)
          // 출입 기록에 학생과 직원이 섞여 있어 직원만 따로 볼 수단이 없었다
          name: '직원 현황',
          href: ownerLayoutService.getOwnerPath('/owner/staff-hub'),
          icon: UserGroupIcon,
        },
        {
          // 총무 인수인계 게시판 (2026-08-10)
          name: '메모 / 전달사항',
          href: ownerLayoutService.getOwnerPath('/owner/staff-memos'),
          icon: ChatBubbleBottomCenterTextIcon,
        },
        {
          name: '직원 관리',
          href: ownerLayoutService.getOwnerPath('/owner/staff'),
          icon: UserGroupIcon,
        },
        {
          name: '총무 업무 편집',
          href: ownerLayoutService.getOwnerPath('/owner/staff-tasks'),
          icon: ClipboardDocumentCheckIcon,
        },
      ],
    },
    {
      title: '설정',
      icon: CogIcon,
      items: [
        {
          name: '시설 정보',
          href: ownerLayoutService.getOwnerPath('/owner/facility-info'),
          icon: BuildingOfficeIcon,
        },
        {
          name: '운영 규칙',
          href: ownerLayoutService.getOwnerPath('/owner/rules'),
          icon: CogIcon,
        },
        {
          name: '과목 관리',
          href: ownerLayoutService.getOwnerPath('/owner/subject-management'),
          icon: BookOpenIcon,
          disabled: true,
          comingSoon: true,
        },
        {
          name: '연동 설정',
          href: ownerLayoutService.getOwnerPath('/owner/integrations'),
          icon: CogIcon,
        },
      ],
    },
    {
      title: '고객지원',
      icon: ExclamationTriangleIcon,
      items: [
        {
          name: '📢 공지사항',
          href: ownerLayoutService.getOwnerPath('/owner/notices'),
          icon: DocumentTextIcon,
        },
        {
          name: '💬 문의하기',
          href: ownerLayoutService.getOwnerPath('/owner/support'),
          icon: ChatBubbleLeftRightIcon,
        },
        // 🐛 오류 제보는 2026-07-27 메뉴에서 내림 — 점주가 "버그인지 질문인지" 판단해야 하는
        // 입구가 둘이면 결국 카톡으로 가버린다(18일간 실제 제보 2건). 입구는 문의하기 하나로.
        // 라우트(/owner/error-reports)와 기존 제보 데이터는 그대로 살아 있고,
        // 진짜 버그는 본부가 문의 화면에서 '🐛 오류로 등록' 버튼으로 올린다.
      ],
    },
  ];

  /**
   * 네비게이션 카테고리 - 로컬 모드에서는 학습 관리 숨김
   */
  const hostname = window.location.hostname;
  const isLocalMode = hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    (hostname === 'localhost' && window.location.port === '8080');

  const localHiddenItems = [
    '통계/리포트',       // 출석 관리
    '그룹 공간 관리',    // 시설 관리
    '운영 규칙',         // 설정
    '직원 관리',         // 설정
    '구독 관리',         // 설정
  ];

  const navigationCategories = isLocalMode
    ? allNavigationCategories
        .filter(cat => cat.title !== '학습 관리')
        .map(cat => ({
          ...cat,
          items: cat.items.filter(item => !localHiddenItems.includes(item.name)),
        }))
        .filter(cat => cat.items.length > 0)
    : allNavigationCategories;

  /**
   * 카테고리 토글
   */
  const toggleCategory = useCallback((title: string) => {
    setExpandedCategories((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  }, []);

  /**
   * 데스크톱 사이드바 토글
   */
  const toggleDesktopSidebar = useCallback(() => {
    setIsDesktopSidebarCollapsed((prev) => !prev);
  }, []);

  /**
   * 로그아웃 핸들러
   */
  const handleLogout = useCallback(() => {
    ownerLayoutService.handleLogout();
    // localhost, 스테이징에서는 /studyroom으로, 운영에서는 독서실 홈으로 리다이렉트
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '192.168.0.38' || hostname === '180.70.238.90') {
      navigate('/studyroom');
    } else if (hostname === 'studyroom.newattend.com') {
      navigate('/');
    } else {
      navigate('/studyroom');
    }
  }, [navigate]);

  /**
   * 전체 알림 수 (기존 알림 + 학부모 알림 + 미출석)
   */
  const totalNotifications =
    ownerLayoutService.getTotalNotifications(notifications) +
    parentNotificationCount +
    absenceCount;

  return {
    expandedCategories,
    isSidebarOpen,
    setIsSidebarOpen,
    isDesktopSidebarCollapsed,
    toggleDesktopSidebar,
    notifications,
    totalNotifications,
    absenceCount,
    organizationName,
    navigationCategories,
    toggleCategory,
    handleLogout,
    // 플랜 관련 정보
    currentPlan,
    availableModules,
    planLoading,
  };
};
